import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

import {
  IoDocumentOutline,
  IoSettingsOutline,
  IoEllipsisHorizontal,
  IoCloseCircleOutline,
} from "react-icons/io5";

import Toaster from "../../components/common/Toaster";
import TableSize from "../../components/common/TableSize";
import Dropdown from "../../components/common/Dropdown";
import Modal from "../../components/common/Modal";

import S2LicenseBreadcrumbs from "./components/S2LicenseBreadcrumbs";
import S2LicenseHeader from "./components/S2LicenseHeader";
import S2LicenseTable from "./components/S2LicenseTable";
import S2LicenseForm from "./components/S2LicenseForm";
import S2LicenseDelete from "./components/S2LicenseDelete";
import S2LicenseFilter from "./components/S2LicenseFilter";

import accountService from "../../services/accountService";

const AccountS2Licenses = () => {
  const { id } = useParams();

  const [data, setData] = useState([]);
  const [info, setInfo] = useState(null);
  const [account, setAccount] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const [tableOptions, setTableOptions] = useState({
    start_date: "",
    end_date: "",
    search_query: "",
    current_page: 1,
    page_size: 10,
    sort_column: { path: "registration_date", order: "desc" },
  });

  const actions = (item) => {
    return [
      [
        {
          name: "View Document",
          icon: <IoDocumentOutline className="mr-3 text-green-500" />,
          is_disabled: !item.s3_reference_name,
          action: () =>
            window.open(
              item.s3_reference_name,
              "_blank",
              "noopener, noreferrer"
            ),
        },
      ],
      [
        {
          name: "Edit Info",
          icon: <IoSettingsOutline className="mr-3 text-gray-400" />,
          action: () => handleEdit(item),
        },
      ],
      [
        {
          name: "Delete S2 License",
          icon: <IoCloseCircleOutline className="mr-3 text-red-500" />,
          action: () => handleDelete(item),
        },
      ],
    ];
  };

  const columns = [
    { path: "number", label: "No." },
    { path: "registration_date", label: "Registration Date" },
    { path: "validity", label: "Validity" },
    {
      key: "actions",
      content: (data) => (
        <Dropdown items={actions(data)} icon={<IoEllipsisHorizontal />} />
      ),
    },
  ];

  const { page_size: size } = tableOptions;

  const settings = [
    {
      id: 1,
      content: <TableSize size={size} onChange={(e) => handleSizeChange(e)} />,
    },
  ];

  const handleOptionsChange = (options) => setTableOptions(options);

  const handleSizeChange = (event) => {
    const page_size = event.target.value;

    if (page_size !== tableOptions.page_size) {
      const options = { ...tableOptions };
      options.page_size = page_size;
      options.current_page = 1;
      setTableOptions(options);
    }
  };

  const handleSearch = (query) => {
    const options = { ...tableOptions };

    if (options.search_query !== query) {
      options.start_date = "";
      options.end_date = "";
      options.search_query = query;
      options.current_page = 1;
      handleOptionsChange(options);
    }
  };

  const handleFilter = (start_date, end_date) => {
    const options = { ...tableOptions };
    options.current_page = 1;
    options.start_date = start_date;
    options.end_date = end_date;
    handleOptionsChange(options);
    setShowFilter(false);
  };

  const handleEdit = (item) => {
    const result = item ?? null;
    setInfo(result);
    setShowForm(!showForm);
  };

  const handleDelete = (item) => {
    const result = item ?? null;
    setInfo(result);
    setShowDelete(!showDelete);
  };

  const handleSuccess = (item) => {
    const index = data.findIndex((i) => i.id === item.id);

    if (index === -1) {
      setData([item, ...data]);
      return;
    }

    const items = [...data];
    items[index] = item;

    setData(items);
  };

  const handleDeleteSuccess = (item) => {
    const items = data.filter((i) => i.id !== item.id);
    setData(items);
    handleDelete();
  };

  const fetchAccount = useCallback(
    async (mounted) => {
      try {
        const result = await accountService.getAccountInfoAsync(id);
        if (result === null) return window.location("/404");

        if (mounted) {
          setAccount(result);
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
      }
    },
    [id]
  );

  useEffect(() => {
    let mounted = true;

    fetchAccount(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchAccount]);

  return (
    <div className="px-6 lg:px-0">
      <S2LicenseBreadcrumbs account={account} />
      <div className="card p-8 mt-5">
        <S2LicenseHeader
          account={account}
          settings={settings}
          onFilter={setShowFilter}
          onSearch={handleSearch}
          onSuccess={handleSuccess}
        />
        <S2LicenseTable
          id={id}
          data={data}
          columns={columns}
          tableOptions={tableOptions}
          setData={setData}
          onChange={handleOptionsChange}
        />
      </div>
      <Modal
        borderTop
        title="Filter"
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        children={<S2LicenseFilter onFilter={handleFilter} />}
      />
      <Modal
        title="Update S2 License"
        isOpen={showForm}
        onClose={() => handleEdit()}
        borderTop
      >
        <S2LicenseForm
          data={info}
          account={account}
          setShowForm={setShowForm}
          onSuccess={handleSuccess}
        />
      </Modal>
      <Modal isOpen={showDelete} onClose={() => handleDelete()}>
        <S2LicenseDelete
          data={info}
          onClose={() => handleDelete()}
          onDelete={handleDeleteSuccess}
        />
      </Modal>
    </div>
  );
};

export default AccountS2Licenses;
